import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { useServerPagination } from '@/hooks/useServerPagination';
import { Search, MapPin, Plus } from 'lucide-react';

export default function GPSTrackers() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);

    const { data: countData } = useQuery({
        queryKey: ['gps-trackers-count', debouncedSearch],
        queryFn: async () => {
            let query = supabase.from('gps_trackers').select('*', { count: 'exact', head: true });
            if (debouncedSearch) {
                query = query.or(`serial_number::text.ilike.%${debouncedSearch}%,sim_number.ilike.%${debouncedSearch}%`);
            }
            const { count, error } = await query;
            if (error) throw error;
            return count || 0;
        },
    });

    const { currentPage, totalPages, totalItems, from, to, goToPage, resetPage } = useServerPagination({
        totalCount: countData || 0
    });

    useEffect(() => {
        resetPage();
    }, [debouncedSearch]);

    const { data: trackers, isLoading } = useQuery({
        queryKey: ['gps-trackers', currentPage, debouncedSearch],
        queryFn: async () => {
            let query = supabase
                .from('gps_trackers')
                .select('*')
                .order('created_at', { ascending: false });
            if (debouncedSearch) {
                query = query.or(`serial_number::text.ilike.%${debouncedSearch}%,sim_number.ilike.%${debouncedSearch}%`);
            }
            query = query.range(from, to);
            const { data, error } = await query;
            if (error) throw error;
            return data;
        },
        enabled: countData !== undefined,
    });

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <MapPin className="h-8 w-8" />
                        <h1 className="text-4xl font-semibold tracking-tight">GPS Trackers</h1>
                    </div>
                    <p className="text-muted-foreground">Manage GPS tracker inventory.</p>
                </div>
                <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Tracker
                </Button>
            </div>

            <Card className="border-0">
                <CardHeader>
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by serial or SIM..."
                                className="pl-9"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                        <ListPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={goToPage}
                            totalItems={totalItems}
                        />
                    {isLoading ? (
                        <Skeleton className="h-48 w-full" />
                    ) : trackers && trackers.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>ID</TableHead>
                                    <TableHead>Serial Number</TableHead>
                                    <TableHead>SIM Number</TableHead>
                                    <TableHead>Added</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {trackers.map((tracker) => (
                                    <TableRow key={tracker.id}>
                                        <TableCell className="font-mono">#{tracker.id}</TableCell>
                                        <TableCell className="font-mono">{tracker.serial_number?.toString() || '-'}</TableCell>
                                        <TableCell>{tracker.sim_number || '-'}</TableCell>
                                        <TableCell>{new Date(tracker.created_at).toLocaleDateString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <p className="text-center text-muted-foreground py-8">No GPS trackers found</p>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
